<template>
  <footer class="py-4 px-2 sm:p-6 bg-base-200/80 backdrop-blur-sm">
    <!-- Payment Method -->
    <div class="mb-4">
      <h3 class="text-base font-medium mb-3">支付方式</h3>
      <van-radio-group
        :model-value="selectedPaymentMethod"
        @update:model-value="emit('update:selectedPaymentMethod', $event)"
      >
        <van-cell-group>
          <van-cell
            v-if="hasWechatPay"
            clickable
            @click="handlePaymentChange(PaymentMethod.Wechat)"
            class="!bg-base-100 !rounded-box mb-3"
          >
            <template #title>
              <div class="flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#05C160"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="mr-3"
                >
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                </svg>
                <span class="font-medium">微信支付</span>
              </div>
            </template>
            <template #right-icon>
              <van-radio :name="PaymentMethod.Wechat" />
            </template>
          </van-cell>

          <van-cell
            clickable
            @click="handlePaymentChange(PaymentMethod.Crystal)"
            :class="['!bg-base-100 !rounded-box']"
          >
            <template #title>
              <div class="flex items-center">
                <CrystalIcon class="mr-3" />
                <span class="font-medium">使用水晶支付</span>
                <span class="ml-2 text-sm text-base-content/70">
                  余额{{ userStore.user.crystalQuantity }}
                  <span
                    v-if="userStore.user.crystalQuantity < selectedPrice.price"
                    class="text-primary cursor-pointer hover:underline"
                    @click.stop="router.push({ name: 'Crystal' })"
                  >
                    去充值
                  </span>
                </span>
              </div>
            </template>
            <template #right-icon>
              <van-radio
                :name="PaymentMethod.Crystal"
                :disabled="userStore.user.crystalQuantity < selectedPrice.price"
              />
            </template>
          </van-cell>
        </van-cell-group>
      </van-radio-group>
    </div>

    <button
      class="btn btn-primary btn-block h-8 rounded-full"
      @click="clickConfirm"
    >
      确认协议并以
      <span class="font-bold flex items-center gap-1">
        <template v-if="selectedPaymentMethod === PaymentMethod.Crystal">
          <CrystalIcon />
        </template>
        <template v-else> ¥ </template>
        {{ selectedPrice.price }}
      </span>
      开通
    </button>
    <div class="form-control items-center mt-3 flex-row">
      <label class="label cursor-pointer">
        <input
          type="checkbox"
          :checked="isAgree"
          class="checkbox checkbox-primary checkbox-xs mr-2"
          @click="isAgree = !isAgree"
        />
      </label>
      <span class="label-text text-xs">
        我接受
        <a
          class="link link-primary"
          @click.stop="openPolicy('user-agreement-cn')"
        >
          《会员服务协议》
        </a>
        与
        <a class="link link-primary" @click.stop="openPolicy('xu-fei-xie-yi')">
          《自动续费协议》
        </a>
      </span>
    </div>
  </footer>

  <ModalPolicyPage v-model:show="showPolicy" :doc-type="policyType" />

  <!-- Payment Success Modal -->
  <VanPopup
    v-model:show="showSuccessPayment"
    :close-on-click-overlay="false"
    round
    class="w-full h-[90%]"
  >
    <div class="bg-base-100 rounded-lg px-2 py-6">
      <!-- Success Icon -->
      <div class="flex justify-center mb-4">
        <div
          class="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-success"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
        </div>
      </div>

      <!-- Title -->
      <h2 class="text-xl font-bold text-base-content mb-4 text-center">
        <span class="text-lg text-base-content">已成为</span>
        <span class="text-lg font-bold text-primary mx-1">
          "{{ activePlanData?.name }}"
        </span>
      </h2>

      <!-- Benefits Section -->
      <div class="bg-base-200 rounded-lg p-6 mb-6">
        <CrystalMemberBenefits :benefits="activePlanData?.benefits || []" />
      </div>

      <!-- Auto Renewal Notice -->
      <!-- <div class="bg-warning/10 border border-warning/20 rounded-lg p-3 mb-6">
        <p class="text-sm text-base-content/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="inline mr-1 text-warning"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          到期将会自动续费，尽情享用吧！
        </p>
      </div> -->

      <!-- Action Buttons -->
      <div class="space-y-3">
        <button
          @click="goToMemberCenter"
          class="btn btn-primary btn-block rounded-full"
        >
          前往会员中心
        </button>
        <button
          @click="goToHome"
          class="btn btn-outline btn-block rounded-full"
        >
          返回
        </button>
      </div>
    </div>
  </VanPopup>

  <ModalPaying
    v-model:show="showPaying"
    :subscription-id="subscriptionId"
    @success="paySuccess"
  />
</template>

<script lang="ts" setup>
import { createOneMemberSubscription } from "@/apis/user";
import { PaymentMethod } from "@/enums/mateEnums";
import { useGlobalUser } from "@/stores/globalUser";
import { showConfirmDialog, showLoadingToast } from "vant";
import { useRouter } from "vue-router";
import { computed, onMounted, ref, watch } from "vue";
import CrystalMemberBenefits from "./CrystalMemberBenefits.vue";
import {
  getMemberTypeByPlanId,
  originPlanData,
} from "@/views/Crystal/memberData";
import { useGlobalModal } from "@/stores/globalModal";
import { routerBack } from "@/use/go";
import { inApp } from "@/modules/common";
import { useAppVersion } from "@/use/common";
import ModalPaying from "./ModalPaying.vue";

const props = defineProps<{
  selectedPrice: { id: number; price: number; wechatPlanId?: number };
  selectedPaymentMethod: PaymentMethod;
}>();

const packageInfo = useAppVersion();
const emit = defineEmits<{
  (e: "update:selectedPrice", value: { id: number; price: number }): void;
  (e: "update:selectedPaymentMethod", value: PaymentMethod): void;
  (e: "pay-finish"): void;
}>();

const userStore = useGlobalUser();
const modalStore = useGlobalModal();
const router = useRouter();

const hasWechatPay = computed(() => {
  return (
    props.selectedPrice.wechatPlanId &&
    inApp &&
    Number(packageInfo?.buildNumber) >= 33
  );
});

watch(
  () => props.selectedPrice.wechatPlanId,
  (newVal) => {
    if (newVal && hasWechatPay.value) {
      emit("update:selectedPaymentMethod", PaymentMethod.Wechat);
    }
  }
);

// Payment success modal state
const showSuccessPayment = ref(false);

const handlePaymentChange = (value: PaymentMethod) => {
  if (
    value === PaymentMethod.Crystal &&
    userStore.user.crystalQuantity < props.selectedPrice.price
  ) {
    return showConfirmDialog({
      message: "水晶余额不足，是否前往充值？",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    })
      .then(() => {
        router.push({ name: "Crystal" });
      })
      .catch(() => {});
  }

  emit("update:selectedPaymentMethod", value);
};

const activePlanData = computed(() => {
  return getPlanData(props.selectedPrice.id);
});

const getPlanData = (id: number) => {
  return originPlanData[getMemberTypeByPlanId(id)!];
};

const clickConfirm = async () => {
  if (!isAgree.value) {
    isAgree.value = true;
  }

  if (!userStore.isLogin) return userStore.openLoginModal();

  if (
    props.selectedPaymentMethod === PaymentMethod.Crystal &&
    userStore.user.crystalQuantity < props.selectedPrice.price
  ) {
    return showConfirmDialog({
      message: "水晶余额不足，是否前往充值？",
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    })
      .then(() => {
        router.push({ name: "Crystal" });
      })
      .catch(() => {});
  }

  const loading = showLoadingToast(`开通中...`);

  const res = await createOneMemberSubscription({
    planId: props.selectedPrice.id,
    paymentMethod: props.selectedPaymentMethod,
  });
  if (props.selectedPaymentMethod === PaymentMethod.Crystal) {
    await userStore.refresh();
    loading.close();
    paySuccess();

    // Show success modal instead of toast
    // showSuccessPayment.value = true;
    // setTimeout(() => {
    //   modalStore.openReceivedModal(
    //     [{ amount: activePlanData.value?.received, type: "powerCube" }],
    //     "paymentReceivedList"
    //   );
    // }, 200);
  } else if (props.selectedPaymentMethod === PaymentMethod.Wechat) {
    loading.close();
    subscriptionId.value = res.data.item.id;
    showPaying.value = true;

    router.replace({
      query: {
        isWechatPay: 1,
        ...Object.fromEntries(
          Object.entries(res.data.paymentConfig.appConfig).map(
            ([key, value]) => [key, encodeURIComponent(value)]
          )
        ),
      },
    });
  }
};

const paySuccess = () => {
  emit("pay-finish");
  showSuccessPayment.value = true;
  setTimeout(() => {
    modalStore.openReceivedModal(
      [{ amount: activePlanData.value?.received, type: "powerCube" }],
      "paymentReceivedList"
    );
  }, 200);
};

// Navigate to member center
const goToMemberCenter = () => {
  showSuccessPayment.value = false;
  router.replace({ name: "memberCenter" });
};

// Navigate to home
const goToHome = () => {
  showSuccessPayment.value = false;
  routerBack();
};

const showPolicy = ref(false);
const policyType = ref("");
const isAgree = ref(true);
const openPolicy = (type: string) => {
  policyType.value = type;
  showPolicy.value = true;
};

const showPaying = ref(false);
const subscriptionId = ref(0);

onMounted(() => {
  if (hasWechatPay.value) {
    emit("update:selectedPaymentMethod", PaymentMethod.Wechat);
  }
});
</script>
